import { Head, router, useForm } from "@inertiajs/react"

import { FileIcon } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import {
  companyDocumentPath,
  companyDocumentsPath,
  editCompanyDocumentPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface DocumentForm {
  id: number
  name: string
  document_date: string | null
  notes: string
  filename: string | null
  file_type: string | null
  file_url: string | null
}

interface Props {
  document: DocumentForm
  can_destroy?: boolean
  errors?: Record<string, string[]>
}

export default function Edit({ document: doc, can_destroy, errors }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Company", href: "/company/resources" },
    { title: "Documents", href: companyDocumentsPath() },
    { title: "Edit Document", href: editCompanyDocumentPath(doc.id) },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    name: doc.name ?? "",
    document_date: doc.document_date ?? "",
    notes: doc.notes ?? "",
    file: null as File | null,
  })

  transform((data) => ({ company_document: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(companyDocumentPath(doc.id), {
      forceFormData: true,
    })
  }

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this document?")) {
      router.delete(companyDocumentPath(doc.id))
    }
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Document" />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Update Document</h1>
          {can_destroy && (
            <button
              type="button"
              onClick={handleDelete}
              className="bg-hpi-red rounded px-4 py-2 text-sm text-white hover:bg-red-700"
            >
              Delete Document
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              value={data.document_date ?? ""}
              onChange={(e) => setData("document_date", e.target.value)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Brief Description</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              maxLength={50}
              required
              className={inputClass}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">{errors.name[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={5}
              className={inputClass}
            />
          </div>

          {doc.filename ? (
            <div>
              <label className={labelClass}>Current File</label>
              <div className="mt-1 flex items-center gap-2 text-sm">
                {doc.file_url ? (
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hpi-active-blue flex items-center gap-2 hover:underline"
                  >
                    <FileIcon fileType={doc.file_type} className="h-5 w-5" />
                    <span>{doc.filename}</span>
                  </a>
                ) : (
                  <div className="text-hpi-dark-gray flex items-center gap-2">
                    <FileIcon fileType={doc.file_type} className="h-5 w-5" />
                    <span>{doc.filename}</span>
                  </div>
                )}
              </div>
              <div className="mt-2">
                <label className="text-hpi-med-gray text-xs">
                  Replace file (optional)
                </label>
                <input
                  type="file"
                  onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
                  className="text-hpi-med-gray file:bg-hpi-pale-blue file:text-hpi-dark-blue hover:file:bg-hpi-light-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className={labelClass}>Select File</label>
              <input
                type="file"
                onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
                className="text-hpi-med-gray file:bg-hpi-pale-blue file:text-hpi-dark-blue hover:file:bg-hpi-light-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
              />
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <a
              href={companyDocumentsPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Save Document"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
